import io from "socket.io-client";
import {
  setParticipants,
  setRoomId,
  setScreenSharedBy,
  toggleScreenShare,
  setChatHistory,
  setNewNotification,
  setAccessToken,
  setIsFlutterAppSharingScreen,
  setFlutterScreenSharedByEmail,
  setFlutterScreenSharedBySocket,
  setFlutterScreenSharedByRoomId,
  setCallEnded,
  setCallerDisconnected,
} from "../slices/roomSlice";
import store from "./../store/index";
import * as webRTCHandler from "./webRTCHandler";
import {
  isVerifiedRecordingBot,
  markBotAudioCallEnded,
  observeBotAudioRoster,
} from "./botAudioBridge";
import { getNavigate } from "./navigatorRef";
const SERVER = process.env.REACT_APP_SOCKET_URL;
const REJOIN_DEBOUNCE_MS = 2000;

let socket = null;
let lastRejoinAttempt = 0;

function attemptCallerRejoin() {
  const { roomId, fromUrl, identity } = store.getState().room;
  if (fromUrl && roomId && identity) {
    const now = Date.now();
    if (now - lastRejoinAttempt < REJOIN_DEBOUNCE_MS) return;
    lastRejoinAttempt = now;
    webRTCHandler.ensureLocalStreamAfterResume().then(() => {
      callerRejoin(roomId, identity);
    });
  }
}

/**
 * Call when browser is back online. Rejoins the call when socket is connected
 * (immediately if already connected, or on next "connect").
 */
export const scheduleCallerRejoinOnNextConnect = () => {
  if (!socket) return;
  const { roomId, fromUrl, identity } = store.getState().room;
  if (!fromUrl || !roomId || !identity) return;
  if (socket.connected) {
    attemptCallerRejoin();
  } else {
    socket.once("connect", attemptCallerRejoin);
  }
};

export const connectWithSocketIOServer = () => {
  socket = io(SERVER);

  socket.on("connect", () => {
    // `connect` fires for both initial connections and reconnects. New calls do
    // not have a roomId yet, so attemptCallerRejoin is a no-op on first connect.
    attemptCallerRejoin();
  });

  socket.on("disconnect", () => {
    try {
      if (typeof window !== "undefined" && window.FlutterOverlay?.restore) {
        window.FlutterOverlay.restore();
      }
    } catch (_) {}
  });

  socket.on("room-id", (data) => {
    // console.log("Room Id", data);
    store.dispatch(setRoomId(data));
  });

  socket.on("room-update", (data) => {
    const { connectedUsers, accessToken  } = data;
    observeBotAudioRoster({
      callId: store.getState().room.roomId,
      socketId: socket.id,
      connectedUsers,
    });
    store.dispatch(setParticipants(connectedUsers));
    if (accessToken) {
      store.dispatch(setAccessToken(accessToken));
    }
    const screenShareActive = store.getState().room.screenShareActive;
    if (screenShareActive) {
      setTimeout(() => {
        socket.emit("room-screen-share-activity", {
          isScreenSharingActive: true,
          roomId: store.getState().room.roomId,
        });

        const screenShareStrem = store.getState().room.screenShareStream;
        webRTCHandler.toggleScreenShare(screenShareActive, screenShareStrem);
      }, 10000);
    }
  });

  socket.on("initiator-disconnected", () => {
    store.dispatch(setCallerDisconnected(true));
  });

  socket.on("initiator-reconnected", () => {
    store.dispatch(setCallerDisconnected(false));
  });

  socket.on("room-error", () => {
    if (isVerifiedRecordingBot()) {
      markBotAudioCallEnded();
      return;
    }
    store.dispatch(setCallEnded());
    const nav = getNavigate();
    if (nav) nav("/call-ended?reason=room-unavailable", { replace: true });
  });

  socket.on("call-expired", () => {
    if (isVerifiedRecordingBot()) {
      markBotAudioCallEnded();
      return;
    }
    store.dispatch(setCallEnded());
    const nav = getNavigate();
    if (nav) nav("/link-expired", { replace: true });
  });

  socket.on("connection-init", (data) => {
    const { connectedUserSocketId } = data;
    // console.log("Connection init", data);
    webRTCHandler.prepareNewPeerConnection(connectedUserSocketId, true);
  });

  socket.on("connection-prepare", (data) => {
    const { connectedUserSocketId } = data;
    // console.log("Connection prepare", data);
    webRTCHandler.prepareNewPeerConnection(connectedUserSocketId, false);

    socket.emit("connection-init", {
      connectedUserSocketId,
    });
  });

  socket.on("connection-signal", (data) => {
    // console.log("Connection signal", data);
    webRTCHandler.handleSinalingData(data);
  });

  socket.on("user-disconnected", (data) => {
    // console.log("User disconnected", data);
    webRTCHandler.removePeerConnection(data);
  });

  socket.on("room-screen-share-activity", (data) => {
    // console.log("Room screen share activity", data);
    const { isScreenSharingActive, socketId, userId } = data;
    store.dispatch(setScreenSharedBy(isScreenSharingActive ? userId : null));
  });

  // Flutter app started sharing its screen
  socket.on("flutter-app-shared-screen", (data) => {
    // Expected data: { email, socketId, roomId }
    console.log("Flutter app started sharing its screen", data);
    try {
      const { email = "", socketId = "", roomId = "" } = data || {};
      store.dispatch(setIsFlutterAppSharingScreen(true));
      store.dispatch(setFlutterScreenSharedByEmail(email));
      store.dispatch(setFlutterScreenSharedBySocket(socketId));
      store.dispatch(setFlutterScreenSharedByRoomId(roomId));
    } catch (e) {
      // no-op
    }
  });

  // Flutter app stopped sharing its screen
  socket.on("flutter-app-stopped-sharing-screen", () => {
    console.log("Flutter app stopped sharing its screen");
    store.dispatch(setIsFlutterAppSharingScreen(false));
    store.dispatch(setFlutterScreenSharedByEmail(""));
    store.dispatch(setFlutterScreenSharedBySocket(""));
    store.dispatch(setFlutterScreenSharedByRoomId(""));
  });

  socket.on("new-message", (data) => {
    // console.log("new-message", data);
    const identity = store.getState().room.identity;
    if (identity != data.identity) store.dispatch(setNewNotification(1));
    store.dispatch(setChatHistory(data));
  });

  socket.on("camera-activity", (data) => {
    // console.log("camera-activity", data);
    const { isCameraActive, socketId } = data;
    const { participants } = store.getState().room;
    const participant = participants.find((p) => p.socketId === socketId);
    // console.log("participant", participant);
    if (participant) {
      const temp = [
        {
          ...participant,
          camera: isCameraActive,
        },
        ...participants.filter((p) => p.socketId !== socketId),
      ];
      store.dispatch(setParticipants(temp));
    }
  });

  socket.on("mute-updated", (data) => {
    observeBotAudioRoster({
      callId: store.getState().room.roomId,
      socketId: socket.id,
      connectedUsers: data.connectedUsers,
    });
    store.dispatch(setParticipants(data.connectedUsers));
  });

  socket.on("end-call-for-others", (data) => {
    // An agent leaving must not make the client end the still-active room.
    if (data?.isCallEnded !== true && data?.reason !== "initiator-disconnected") return;
    if (isVerifiedRecordingBot()) {
      markBotAudioCallEnded();
      // The recorder server must drain its audio before closing the bot page.
      return;
    }
    store.dispatch(setCallEnded());
    const nav = getNavigate();
    if (nav) {
      nav(
        data?.reason === "initiator-disconnected"
          ? "/call-ended?reason=caller-left"
          : "/call-ended",
        { replace: true }
      );
    }
  });

};

export const createNewRoom = (data) => {
  const { identity, calleeEmail } = store.getState().room;
  const payload = {
    ...data,
    userName: (identity || data.identity || "").trim(),
    userEmail: (calleeEmail || "").trim(),
    team_member_id: data.selected_support_team_member_id,
  };
  socket.emit("create-new-room", payload);
};

export const joinRoom = (roomId, identity, attendee_token, is_selected_agent) => {
  const data = {
    roomId,
    identity,
    attendee_token,
    caller_id: roomId,
    camera: false,
  };
  if (is_selected_agent !== undefined) {
    data.is_selected_agent = is_selected_agent;
  }
  socket.emit("join-room", data);
};

export const callerRejoin = (roomId, identity) => {
  if (socket) {
    const { calleeEmail } = store.getState().room;
    socket.emit("caller-rejoin", {
      roomId,
      identity,
      userName: (identity || "").trim(),
      userEmail: (calleeEmail || "").trim(),
    });
  }
};

export const signalPeerData = (data) => {
  // console.log("Signal peer data", data);
  socket.emit("connection-signal", data);
};

export const saveSentimentData = (callId, text, emotion) => {
  const data = {
    callId: callId,
    text,
    emotion,
    timestamp: Date.now(),
  }

  socket.emit("sentiment-data", data);
}



export const notifyRoomParticipantsForScreenShare = (isScreenSharingActive) => {
  // console.log(
  // "Notify room participants for screen share",
  //   isScreenSharingActive
  // );
  const roomId = store.getState().room.roomId;
  socket.emit("room-screen-share-activity", {
    isScreenSharingActive,
    roomId,
  });
};

export const sendMessage = (data) => {
  socket.emit("send-message", data);
};

export const endCall = (data) => {
  socket.emit("end-call", data);
};

export const updateCameraStatus = (isCameraActive) => {
  const data = {
    isCameraActive,
    roomId: store.getState().room.roomId,
  };
  if (socket) {
    socket.emit("camera-activity", data);
  }
};
export const updateMute = (data) => {
  socket.emit("update-mute", data);
};

/** Absolute mute flag for server `connectedUsers[].isMuted` (remote tiles / recorder UI). */
export const syncMicMutedToServer = (muted) => {
  const roomId = store.getState().room.roomId;
  if (socket && roomId) {
    socket.emit("update-mute", { roomId, muted });
  }
};

export const startAudioRecordingForAll = (roomId) => {
  console.log("start-audio-recording");
  console.log("RTORC") //Removed the old recording code
  return;
  if (socket) {
    socket.emit("start-audio-recording", {
      roomId,
    });
  }
};
